import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import store from "../../stateManagement/store";
import { closeOtpPopup } from "../../stateManagement/actions/validateOtpPopupActions";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import productionImage from "../../assets/image/png/production.png";
import { MuiOtpInput } from "mui-one-time-password-input";
import { ChannelkartNetworkGet, ChannelkartNetworkPost, getNetworkServiceFunction } from "../../services/networkService";
import { sendAuthOtp as sendOtp, verifyOtp, getAuthServiceState, createOtpRequest } from "../../services/salescodeAuthService";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { dialCodeToISOCode } from "../countrySelect/CountryCodes";
import "./validateOtpPopup.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { manageUpdateAccessObj } from "../../features/manageUpdateAccess/manageUpdateAccessTypes";

import { Loader } from "../loader/Loader";
import { TranslationEnum, usePortalTranslation } from "../../providers/TranslationProvider";
const otpTextFieldsProps: TextFieldProps = {
  style: {
    background: "white",
    borderRadius: "4px",
  },
  inputProps: {
    style: {
      padding: "5px 0",
      color: "#909090",
      fontSize: "20px",
      fontWeight: 800,
    },
  },
};
function ValidateOtpPopup() {
  const CUR_COMPONENT = "ValidateOtp"
  const { translate } = usePortalTranslation();
  const otpPopupState = useSelector(() => store.getState().otpPopupState);
  const [otp, setOtp] = useState("");
  const [users, setUsers] = useState<manageUpdateAccessObj[]>([]);
  const defaultSelectedOption = { phoneNumber: '', isAdmin: false , countryCode: '', userName: '' };
  const [selectedUser, setSelectedUser] = useState<manageUpdateAccessObj>(defaultSelectedOption);
  const [otpGenerated, setOtpGenerated] = useState<boolean>(false);
  const [showLoader,setShowLoader] = useState<boolean>(false);
  const [verifyLoader,setVerifyLoader] = useState<boolean>(false);
  const [otpLength, setOtpLength] = useState(6);
  const sessionIdRef = useRef<string>("");
  const senderIdRef = useRef<string>("");
  const updateOtp = (newValue: string) => {
    setOtp(newValue);
  };

  useEffect(() => {
      (async () => {
          try {
              const response = await ChannelkartNetworkGet(
                  "/metadata/clientconfig?name=authentication_configuration"
              );
              if (
                  response?.status === 200 &&
                  response?.data?.features &&
                  response.data.features.length > 0 &&
                  response.data.features[0].domainValues
              ) {
                  const configData = (
                      response.data.features[0].domainValues as any[]
                  ).find((ele) => {
                      return ele.name === "otpLength";
                  });

                  if (configData?.value) {
                      setOtpLength(parseInt(configData?.value.toString()));
                  }
              }
          } catch (e) {}
      })();
  }, [otpPopupState.isOpen]);

  const closeModal = () => {
    closeOtpPopup();
    setOtpGenerated(false);
    setOtp("");
    setSelectedUser(defaultSelectedOption);
    setShowLoader(false);
  };
  const [imageSrc, setImageSrc] = useState("");
  useEffect(() => {
    const img = new Image();
    img.src = productionImage;
    img.onload = () => setImageSrc(productionImage);
  }, []);

  async function validateOtp() {
    try {
      setVerifyLoader(true);
      const authServiceState = await getAuthServiceState();
      const isAuthServiceEnabled = authServiceState.isAuthService;

      if(otpPopupState.api && otpPopupState.verifyType){
        const networkServiceFn = getNetworkServiceFunction(otpPopupState.verifyType);
        const headers: {
          sessionId?: string;
          otp: any;
          id?: string;
          otpContext?: string;
        } = {
          otp
        };

        if (isAuthServiceEnabled) {
          headers.otpContext = sessionIdRef.current;
        } else {
          headers.sessionId = sessionIdRef.current;
        }

        if (senderIdRef.current && senderIdRef.current !== "") {
          headers.id = senderIdRef.current;
        }

        const response = await networkServiceFn(otpPopupState.api,otpPopupState.apiPayload,headers);
        if(response.status>=200 && response.status<300){
          otpPopupState.onSuccess(selectedUser,response);
          closeModal();
        }else{
          openPopup("Error",response?.data?.message ?? (response.status===401?"Something went wrong while submitting OTP" : "Something went wrong"));
        }
      }else{
        ChannelkartNetworkPost("/v1/otp/verify", {
          mobileNumber: selectedUser?.phoneNumber,
          otp: otp,
          countryCode: selectedUser?.countryCode,
          sessionId: sessionIdRef.current
        }).then((response)=>{
            if(response.status==200){
                otpPopupState.onSuccess(selectedUser,response);
                closeModal()
            }else{
                openPopup("Alert","This OTP does not exist.")
            }
        });
      }

    } catch (err: any) {
      openPopup("Error", err?.data?.message ?? "something went wrong");
    }finally{
      setVerifyLoader(false);
    }
  }

  async function sendOtpForAuthentication(mobileNumber: string,countryCode: string) {
    try {
      if(otpPopupState.api && otpPopupState.verifyType){
        const authServiceState = await getAuthServiceState();
        const isAuthServiceEnabled = authServiceState.isAuthService;

        if (isAuthServiceEnabled) {
          // Convert dial code to ISO country code (e.g., "91" -> "IN")
          const isoCountryCode = dialCodeToISOCode(countryCode);
          const otpRequest = createOtpRequest('SMS', mobileNumber, {
            countryCode: isoCountryCode
          });

          const response = await sendOtp(otpRequest, 'SMS');
          if (response?.status === 'PENDING') {
            sessionIdRef.current = response.otpContext || '';
            setOtpGenerated(true);
          } else {
            const defaultMessage = "Something went wrong while sending otp";
            openPopup("Error", response?.message ?? defaultMessage);
            closeModal();
          }
        } else {
          const resourceURL = otpPopupState.api.split("?")?.[0];
          ChannelkartNetworkPost("/v1/otp/send/resource", {
            mobileNumber: mobileNumber,
            countryCode: countryCode,
            resourceURL
          }).then((response) => {
            if (response.status!== 200 && response.status!==202) {
              const defaultMessage = "Something went wrong while sending otp";
              openPopup("Error", response?.data?.responseText ?? defaultMessage);
              closeModal();
            }else{
              sessionIdRef.current = response?.data?.response?.sessionId ?? response?.data?.response?.token;
              senderIdRef.current = response?.data?.response?.id;
              setOtpGenerated(true);
            }
          });
        }
      }else{
        ChannelkartNetworkPost("/v1/otp/send", {
          mobileNumber: mobileNumber,
          countryCode: countryCode,
        }).then((response) => {
          if (response.status!== 200 && response.status!==202) {
            openPopup("Error", response?.data?.responseText);
            closeModal();
          }else{
            sessionIdRef.current = response?.data?.sessionId;
            setOtpGenerated(true);
          }
        });
      }
    } catch (err) {
      closeModal();
      openPopup("Error", "something went wrong");
    }
  }

  useEffect(() => {
    try {
      if (otpPopupState.isOpen) {
        setUsers(otpPopupState.allowedUsers);
      }
    } catch (err) {
      console.log(err);
      openPopup("Error", "Something went wrong");
    }
  }, [otpPopupState.isOpen]);

  const validateChar = (value: string, index: number) => {
    return !isNaN(Number(value)) && value != " ";
  };

  return (
    <div>
      <Backdrop
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={otpPopupState.isOpen}
      >
        {!showLoader && otpPopupState.isOpen ?<>
          <div className="validation_popup_container" style={{
            width: otpPopupState.newUI ? "40%" : "35%"
          }}>
            <div className="otp_popup_cross_container">
              <FontAwesomeIcon
                className="otp_cross_button"
                icon={faClose}
                onClick={closeModal}
              />
            </div>
            {otpPopupState.newUI &&
            <div style={{display:"flex",alignItems:"center",flexDirection:"column"}}>
              <img
                  src={imageSrc}
                  alt="Production"
                  style={{ width: "20%", borderRadius: "8px" }}
                />
              <div style={{ display: "flex",justifyContent:"center",textAlign:"center",fontSize:"20px",fontWeight:600 }}>
                Congratulations!<br />
                Your app is now ready to use
              </div>
              </div>
            }
            <Box sx={{ p: 2 , mb: 1 }}>
              <Autocomplete
                disabled={otpGenerated}
                disableClearable
                sx={{ width: "100%" }}
                onChange={(event, value) => {
                  setSelectedUser(value);
                }}
                value={selectedUser}
                getOptionLabel={(option) => option.userName? option.userName + " - " + option.phoneNumber : option.phoneNumber}
                options={users}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translate(TranslationEnum.validate_otp,"Select a number to verify")}
                    placeholder={translate(TranslationEnum.validate_otp,"Select phone number")}
                  />
                )}
              />
            </Box>
            {!otpGenerated?<Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  className="validate_otp_button"
                  disabled={!Boolean(selectedUser.phoneNumber)}
                  variant="contained"
                  size="large"
                  onClick={() => {
                    sendOtpForAuthentication(selectedUser?.phoneNumber,selectedUser?.countryCode)
                  }}
                >
                  {translate(TranslationEnum.validate_otp,"GENERATE OTP")}
                </Button>
              </Box>:<>

              <Box sx={{ p: 2, mb: 1}}>
                <label className="validate-otp-popup-label"> {translate(TranslationEnum.validate_otp,"Please enter the OTP")}</label>
                <MuiOtpInput
                  TextFieldsProps={otpTextFieldsProps}
                  className="validation_otp_input"
                  id="otp"
                  value={otp}
                  length={otpLength}
                  onChange={updateOtp}
                  validateChar={validateChar}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  className="validate_otp_button"
                  disabled={otp.length !== otpLength || verifyLoader}
                  variant="contained"
                  size="large"
                  onClick={validateOtp}
                >
                   {verifyLoader?<div className="verify-OTP-circular-progress"><CircularProgress size={15} color="inherit"/>{translate(TranslationEnum.validate_otp,"VERIFY")}</div> : <>{translate(TranslationEnum.validate_otp,"VERIFY")}</> }
                </Button>
              </Box>
              </>}
            {otpPopupState.newUI && <Box
              sx={{
                margin: "auto",
                marginTop: "20px",
                display: "flex",
                alignItems: "center",
                padding: "8px 16px",
                backgroundColor: "#fff7e6",
                borderRadius: "8px",
                fontSize: "14px",
                width: "fit-content",
              }}
            >
              <InfoIcon
                sx={{
                  color: "#f2a03b",
                  marginRight: "8px",
                  fontSize: "16px",
                }}
              />
              <Typography
                sx={{
                  color: "#000",
                  fontSize: "12px",
                  whiteSpace: "nowrap"
                }}
              >
                After the OTP verification, you will be redirected to the Salescode Portal.
              </Typography>
            </Box>}
          </div>
        </>:<><Loader /></>}
      </Backdrop>
    </div>
  );
}

export default ValidateOtpPopup;
